import { useEffect, useRef, useState } from "react";
import { cameraService } from "client-app-core";
import moment from "moment";

const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;
const DAYS_IN_WEEK = 7;

/**
 * Returns the width/height that fit a given aspect ratio entirely inside a container element.
 * Chooses the constraining side (width or height) so the box never overflows the container.
 */
function fitAspectInsideContainer(containerEl, aspectW = 16, aspectH = 9) {
	const containerWidth = containerEl.offsetWidth;
	const containerHeight = containerEl.offsetHeight;

	const targetRatio = aspectW / aspectH;

	if (containerHeight === 0) {
		// If height is 0, calculate height from width and aspect ratio
		const width = containerWidth;
		const height = width / targetRatio;
		return { width, height };
	}

	const containerRatio = containerWidth / containerHeight;

	let width, height;

	if (containerRatio > targetRatio) {
		// Container is wider than aspect ratio, fit to height
		height = containerHeight;
		width = height * targetRatio;
	} else {
		// Container is taller or matches aspect ratio, fit to width
		width = containerWidth;
		height = width / targetRatio;
	}

	return { width, height };
}

function useElementSize() {
	const ref = useRef(null);
	const [size, setSize] = useState({ width: 0, height: 0 });
	const [changeCount, setChangeCount] = useState(0);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		let prev = { width: 0, height: 0 };

		const measure = () => {
			const r = el.getBoundingClientRect();
			if (r.width !== prev.width || r.height !== prev.height) {
				setSize({ width: r.width, height: r.height });
				setChangeCount((c) => c + 1);
				prev = { width: r.width, height: r.height };
			}
		};

		// initial measure
		measure();

		// Fallback if ResizeObserver isn’t available
		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", measure);
			return () => window.removeEventListener("resize", measure);
		}

		const ro = new ResizeObserver((entries) => {
			const cr = entries[0].contentRect;
			if (cr.width !== prev.width || cr.height !== prev.height) {
				setSize({ width: cr.width, height: cr.height });
				setChangeCount((c) => c + 1);
				prev = { width: cr.width, height: cr.height };
			}
		});

		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	return { bccContainerRef: ref, size, updateDimensions: changeCount };
}

const getStreamURL = (cameraId) => {
	return new Promise((resolve) => {
		cameraService.getStreamURL(cameraId, (err, response) => {
			if (err) {
				console.log("ERROR", err);
				resolve(null);
			} else if (response) {
				const { streamUrl } = response;
				resolve(streamUrl);
			} else {
				resolve(null);
			}
		});
	});
};

const getPlaybackURL = (cameraId, start, end) => {
	return new Promise((resolve) => {
		cameraService.getPlaybackURL(cameraId, start, end, (err, response) => {
			if (err) {
				console.log("ERROR", err);
				resolve(null);
			} else if (response) {
				const { playbackUrl } = response;
				resolve(playbackUrl);
			} else {
				resolve(null);
			}
		});
	});
};

const usePrevious = (value) => {
	const ref = useRef(null);
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

const getActualDuration = value => {
	if (value >= DAYS_IN_WEEK * HOURS_IN_DAY * MINUTES_IN_HOUR) {
		const weeks = Math.floor(value / (DAYS_IN_WEEK * HOURS_IN_DAY * MINUTES_IN_HOUR));
		return { value: weeks, type: "weeks" };
	} else if (value >= HOURS_IN_DAY * MINUTES_IN_HOUR) {
		const days = Math.floor(value / (HOURS_IN_DAY * MINUTES_IN_HOUR));
		return { value: days, type: "days" };
	} else if (value >= MINUTES_IN_HOUR) {
		const hours = Math.floor(value / MINUTES_IN_HOUR);
		return { value: hours, type: "hours" };
	} else {
		return { value, type: "minutes" };
	}
};

const calcuateDateTimeDifference = (toSubtract, type, dateTime) => {
	return moment(dateTime).subtract(toSubtract, type);
};

export { fitAspectInsideContainer, useElementSize, getStreamURL, getPlaybackURL, usePrevious, getActualDuration, calcuateDateTimeDifference };
